import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, FilterIcon, UsersIcon, GraduationCapIcon, CalendarIcon, GridIcon, ListIcon, SchoolIcon, AlertCircleIcon, DownloadIcon, FileTextIcon, Loader2Icon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { StudentProfilePanel } from '../components/StudentProfilePanel';
// Types
interface Guardian {
  id: string;
  name: string;
  phone?: string;
  whatsapp?: string;
  verified: boolean;
}
interface Enrollment {
  id: string;
  classroomId: string;
  classroomName: string;
  academicYear: string;
  enrolledAt: string;
  leftAt?: string;
  status: 'active' | 'ended';
}
interface Student {
  id: string;
  name: string;
  email: string;
  externalStudentId?: string;
  status: 'enrolled' | 'not-enrolled';
  leftOrganization?: boolean;
  createdAt: string;
  guardian?: Guardian;
  enrollments: Enrollment[];
  currentGrade?: string;
}
export function OrganizationStudents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'enrolled' | 'not-enrolled'>('enrolled');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [academicYearFilter, setAcademicYearFilter] = useState<string>('all');
  const [classroomFilter, setClassroomFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showStudentPanel, setShowStudentPanel] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Mock Data
  const [students] = useState<Student[]>([{
    id: '1',
    name: 'Alice Johnson',
    email: 'alice.johnson@school.edu',
    externalStudentId: 'STU-2024-001',
    status: 'enrolled',
    leftOrganization: false,
    createdAt: '2024-08-15',
    currentGrade: 'Grade 7',
    guardian: {
      id: 'g1',
      name: 'Mary Johnson',
      phone: '+1 (555) 123-4567',
      whatsapp: '+15551234567',
      verified: true
    },
    enrollments: [{
      id: 'e1',
      classroomId: '1',
      classroomName: 'Grade 7A',
      academicYear: '2024-2025',
      enrolledAt: '2024-08-15',
      status: 'active'
    }, {
      id: 'e2',
      classroomId: '5',
      classroomName: 'Grade 6A',
      academicYear: '2023-2024',
      enrolledAt: '2023-08-20',
      leftAt: '2024-06-15',
      status: 'ended'
    }]
  }, {
    id: '2',
    name: 'Bob Smith',
    email: 'bob.smith@school.edu',
    externalStudentId: 'STU-2024-002',
    status: 'enrolled',
    leftOrganization: false,
    createdAt: '2024-08-16',
    currentGrade: 'Grade 7',
    guardian: {
      id: 'g3',
      name: 'Sarah Smith',
      phone: '+1 (555) 234-5678',
      verified: false
    },
    enrollments: [{
      id: 'e3',
      classroomId: '1',
      classroomName: 'Grade 7A',
      academicYear: '2024-2025',
      enrolledAt: '2024-08-16',
      status: 'active'
    }]
  }, {
    id: '3',
    name: 'Charlie Brown',
    email: 'charlie.brown@school.edu',
    externalStudentId: 'STU-2024-003',
    status: 'enrolled',
    leftOrganization: false,
    createdAt: '2024-08-17',
    currentGrade: 'Grade 8',
    enrollments: [{
      id: 'e4',
      classroomId: '2',
      classroomName: 'Grade 8B',
      academicYear: '2024-2025',
      enrolledAt: '2024-08-17',
      status: 'active'
    }]
  }, {
    id: '4',
    name: 'Diana Martinez',
    email: 'diana.m@school.edu',
    status: 'not-enrolled',
    leftOrganization: true,
    createdAt: '2023-08-10',
    currentGrade: 'Grade 7',
    guardian: {
      id: 'g4',
      name: 'Carlos Martinez',
      phone: '+1 (555) 345-6789',
      whatsapp: '+15553456789',
      verified: true
    },
    enrollments: [{
      id: 'e5',
      classroomId: '6',
      classroomName: 'Grade 6B',
      academicYear: '2023-2024',
      enrolledAt: '2023-08-10',
      leftAt: '2024-06-15',
      status: 'ended'
    }]
  }]);
  const allGrades = Array.from(new Set(students.map(s => s.currentGrade).filter(Boolean))).sort();
  const allAcademicYears = Array.from(new Set(students.flatMap(s => s.enrollments.map(e => e.academicYear)))).sort().reverse();
  const allClassrooms = Array.from(new Set(students.flatMap(s => s.enrollments.map(e => e.classroomName)))).sort();
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);
  // Handle studentId from URL query params
  useEffect(() => {
    const studentId = searchParams.get('studentId');
    if (studentId && !isLoading) {
      const student = students.find(s => s.id === studentId);
      if (student) {
        setSelectedStudent(student);
        setShowStudentPanel(true);
        // Clear the query param after opening
        setSearchParams({});
      }
    }
  }, [searchParams, students, isLoading, setSearchParams]);
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('cards');
      } else {
        setViewMode('table');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase()) || student.email.toLowerCase().includes(searchQuery.toLowerCase()) || student.externalStudentId?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
    const matchesGrade = gradeFilter === 'all' || student.currentGrade === gradeFilter;
    const matchesAcademicYear = academicYearFilter === 'all' || student.enrollments.some(e => e.academicYear === academicYearFilter);
    const matchesClassroom = classroomFilter === 'all' || student.enrollments.some(e => e.classroomName === classroomFilter);
    return matchesSearch && matchesStatus && matchesGrade && matchesAcademicYear && matchesClassroom;
  });
  const handleStudentClick = (student: Student) => {
    setSelectedStudent(student);
    setShowStudentPanel(true);
  };
  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
    }, 1500);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };
  return <Tooltip.Provider delayDuration={200} data-id="element-3567">
      <div className="flex flex-col min-h-screen bg-background" data-id="element-3568">
        <Header data-id="element-3569" />
        <div className="flex flex-1 overflow-hidden" data-id="element-3570">
          <div className="hidden md:block" data-id="element-3571">
            <Sidebar data-id="element-3572" />
          </div>
          <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-3573">
            <main className="flex-1" data-id="element-3574">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-3575">
                {/* Page Header */}
                <div className="mb-6" data-id="element-3576">
                  <div className="flex items-start justify-between gap-4 mb-4" data-id="element-3577">
                    <div className="flex-1" data-id="element-3578">
                      <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2" data-id="element-3579">
                        Students
                      </h1>
                      <p className="text-sm sm:text-base text-text-secondary" data-id="element-3580">
                        Manage student records, enrollments, and parent/guardian
                        contacts across all academic years.
                      </p>
                    </div>

                    <div className="flex gap-2" data-id="element-3581">
                      <DropdownMenu.Root data-id="element-3582">
                        <DropdownMenu.Trigger asChild data-id="element-3583">
                          <button className="p-2.5 bg-surface border-2 border-custom text-text-primary rounded-xl font-semibold hover:bg-surface-elevated transition-colors" aria-label="Export students" data-id="element-3584">
                            {isExporting ? <Loader2Icon size={20} className="animate-spin" data-id="element-3585" /> : <DownloadIcon size={20} data-id="element-3586" />}
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal data-id="element-3587">
                          <DropdownMenu.Content className="min-w-[160px] bg-surface-elevated rounded-xl border border-custom shadow-custom-lg p-1 z-50" sideOffset={5} align="end" data-id="element-3588">
                            <DropdownMenu.Item className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" onSelect={handleExport} data-id="element-3589">
                              <FileTextIcon size={16} data-id="element-3590" /> Export as CSV
                            </DropdownMenu.Item>
                            <DropdownMenu.Item className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" onSelect={handleExport} data-id="element-3591">
                              <FileTextIcon size={16} data-id="element-3592" /> Export as PDF
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div>
                  </div>
                </div>

                {/* Info Banner */}
                <div className="mb-6 bg-[#2563EB]/5 border border-[#2563EB]/20 rounded-xl p-4" data-id="element-3593">
                  <div className="flex items-start gap-3" data-id="element-3594">
                    <div className="w-8 h-8 rounded-full bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0" data-id="element-3595">
                      <AlertCircleIcon size={16} className="text-[#2563EB]" data-id="element-3596" />
                    </div>
                    <div className="flex-1" data-id="element-3597">
                      <h3 className="text-sm font-bold text-text-primary mb-1" data-id="element-3598">
                        Persistent Student Records
                      </h3>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-3599">
                        Student records are permanent and independent of
                        classrooms. When students are enrolled in classrooms, an
                        enrollment record is created. Archiving a classroom ends
                        enrollments but preserves all student data and history.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Filters */}
                <div className="mb-6 space-y-3" data-id="element-3600">
                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-3601">
                    <div className="relative w-full sm:flex-1" data-id="element-3602">
                      <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary" data-id="element-3603" />
                      <input type="text" placeholder="Search by name, email, or student ID" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary placeholder-text-tertiary" data-id="element-3604" />
                    </div>

                    <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1 sm:pb-0" data-id="element-3605">
                      <div className="relative min-w-[120px] sm:min-w-[140px] flex-shrink-0" data-id="element-3606">
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="w-full px-3 sm:px-4 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3607">
                          <option value="enrolled" data-id="element-3608">Enrolled</option>
                          <option value="not-enrolled" data-id="element-3609">Not Enrolled</option>
                          <option value="all" data-id="element-3610">All Status</option>
                        </select>
                      </div>

                      {/* View Mode Toggle (Desktop only) */}
                      <div className="hidden md:flex bg-surface border-2 border-custom rounded-xl p-1" data-id="element-3611">
                        <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3612">
                          <ListIcon size={18} data-id="element-3613" />
                        </button>
                        <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'cards' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3614">
                          <GridIcon size={18} data-id="element-3615" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Advanced Filters */}
                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-3616">
                    <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1 sm:pb-0 flex-1" data-id="element-3617">
                      <div className="relative min-w-[140px] sm:min-w-[160px] flex-shrink-0" data-id="element-3618">
                        <GraduationCapIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3619" />
                        <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)} className="w-full pl-9 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3620">
                          <option value="all" data-id="element-3621">All Grades</option>
                          {allGrades.map(grade => <option key={grade} value={grade} data-id="element-3622">
                              {grade}
                            </option>)}
                        </select>
                      </div>

                      <div className="relative min-w-[140px] sm:min-w-[160px] flex-shrink-0" data-id="element-3623">
                        <CalendarIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3624" />
                        <select value={academicYearFilter} onChange={e => setAcademicYearFilter(e.target.value)} className="w-full pl-9 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3625">
                          <option value="all" data-id="element-3626">All Years</option>
                          {allAcademicYears.map(year => <option key={year} value={year} data-id="element-3627">
                              {year}
                            </option>)}
                        </select>
                      </div>

                      <div className="relative min-w-[140px] sm:min-w-[160px] flex-shrink-0" data-id="element-3628">
                        <SchoolIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3629" />
                        <select value={classroomFilter} onChange={e => setClassroomFilter(e.target.value)} className="w-full pl-9 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3630">
                          <option value="all" data-id="element-3631">All Classrooms</option>
                          {allClassrooms.map(classroom => <option key={classroom} value={classroom} data-id="element-3632">
                              {classroom}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    {(gradeFilter !== 'all' || academicYearFilter !== 'all' || classroomFilter !== 'all' || statusFilter !== 'enrolled') && <button onClick={() => {
                    setGradeFilter('all');
                    setAcademicYearFilter('all');
                    setClassroomFilter('all');
                    setStatusFilter('enrolled');
                  }} className="w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-text-secondary hover:text-text-primary hover:bg-surface border-2 border-custom rounded-xl transition-colors whitespace-nowrap" data-id="element-3633">
                        Clear Filters
                      </button>}
                  </div>
                </div>

                {/* Students List */}
                {isLoading ? <div className="animate-pulse space-y-4" data-id="element-3634">
                    {[1, 2, 3, 4, 5].map(i => <div key={i} className="flex items-center space-x-4 p-4 bg-surface border border-custom rounded-xl" data-id="element-3635">
                        <div className="h-10 w-10 rounded-full bg-gray-200" data-id="element-3636" />
                        <div className="flex-1 space-y-2" data-id="element-3637">
                          <div className="h-4 bg-gray-200 rounded w-1/4" data-id="element-3638" />
                          <div className="h-3 bg-gray-200 rounded w-1/3" data-id="element-3639" />
                        </div>
                      </div>)}
                  </div> : filteredStudents.length === 0 ? <div className="text-center py-12 bg-surface-elevated rounded-xl border border-custom" data-id="element-3640">
                    <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3641">
                      <SearchIcon size={32} className="text-text-tertiary" data-id="element-3642" />
                    </div>
                    <h3 className="text-lg font-bold text-text-primary mb-2" data-id="element-3643">
                      No students found
                    </h3>
                    <p className="text-text-secondary" data-id="element-3644">
                      Try adjusting your filters or search query.
                    </p>
                  </div> : viewMode === 'cards' ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3645">
                    {filteredStudents.map(student => <div key={student.id} onClick={() => handleStudentClick(student)} className="bg-surface border border-custom rounded-xl p-4 space-y-4 hover:border-[#00B75F] transition-colors cursor-pointer" data-id="element-3646">
                        <div className="flex items-start justify-between" data-id="element-3647">
                          <div className="flex items-center gap-3" data-id="element-3648">
                            <div className="w-10 h-10 rounded-full bg-[#00B75F] bg-opacity-10 text-[#00B75F] flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-3649">
                              {student.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div data-id="element-3650">
                              <h3 className="font-bold text-text-primary" data-id="element-3651">
                                {student.name}
                              </h3>
                              <p className="text-xs text-text-secondary" data-id="element-3652">
                                {student.email}
                              </p>
                            </div>
                          </div>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold whitespace-nowrap ${student.status === 'enrolled' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'bg-gray-100 text-gray-500'}`} data-id="element-3653">
                            {student.status === 'enrolled' ? <>
                                <div className="w-2 h-2 rounded-full bg-[#00B75F]" data-id="element-3654" />
                                Enrolled
                              </> : 'Not Enrolled'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-3655">
                          <div className="flex flex-col" data-id="element-3656">
                            <span className="text-text-tertiary" data-id="element-3657">
                              Student ID
                            </span>
                            <span className="text-text-primary font-medium" data-id="element-3658">
                              {student.externalStudentId || '—'}
                            </span>
                          </div>
                          <div className="flex flex-col" data-id="element-3659">
                            <span className="text-text-tertiary" data-id="element-3660">
                              Current Grade
                            </span>
                            <span className="text-text-primary" data-id="element-3661">
                              {student.currentGrade || '—'}
                            </span>
                          </div>
                          <div className="flex flex-col" data-id="element-3662">
                            <span className="text-text-tertiary" data-id="element-3663">
                              Enrollments
                            </span>
                            <span className="text-text-primary font-medium" data-id="element-3664">
                              {student.enrollments.filter(e => e.status === 'active').length}{' '}
                              active
                            </span>
                          </div>
                          <div className="flex flex-col" data-id="element-3665">
                            <span className="text-text-tertiary" data-id="element-3666">
                              Guardians
                            </span>
                            <span className="text-text-primary font-medium" data-id="element-3667">
                              {student.guardian ? '1' : '0'}
                            </span>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="bg-surface-elevated rounded-xl border border-custom overflow-hidden" data-id="element-3668">
                    <div className="overflow-x-auto" data-id="element-3669">
                      <table className="w-full" data-id="element-3670">
                        <thead className="bg-surface border-b border-custom" data-id="element-3671">
                          <tr data-id="element-3672">
                            <th className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider w-64" data-id="element-3673">
                              Name
                            </th>
                            <th className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-3674">
                              Email
                            </th>
                            <th className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-3675">
                              Student ID
                            </th>
                            <th className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-3676">
                              Grade
                            </th>
                            <th className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-3677">
                              Enrollments
                            </th>
                            <th className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-3678">
                              Guardians
                            </th>
                            <th className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-3679">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-custom" data-id="element-3680">
                          {filteredStudents.map(student => <tr key={student.id} onClick={() => handleStudentClick(student)} className="hover:bg-surface transition-colors cursor-pointer" data-id="element-3681">
                              <td className="px-6 py-4 w-64" data-id="element-3682">
                                <div className="flex items-center gap-3" data-id="element-3683">
                                  <div className="w-10 h-10 rounded-full bg-[#00B75F] bg-opacity-10 text-[#00B75F] flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-3684">
                                    {student.name.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <span className="font-medium text-text-primary" data-id="element-3685">
                                    {student.name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-text-secondary" data-id="element-3686">
                                {student.email}
                              </td>
                              <td className="px-6 py-4 text-sm text-text-secondary font-mono" data-id="element-3687">
                                {student.externalStudentId || '—'}
                              </td>
                              <td className="px-6 py-4 text-sm text-text-secondary" data-id="element-3688">
                                {student.currentGrade || '—'}
                              </td>
                              <td className="px-6 py-4" data-id="element-3689">
                                <span className="text-sm font-medium text-text-primary" data-id="element-3690">
                                  {student.enrollments.filter(e => e.status === 'active').length}{' '}
                                  active
                                </span>
                                <span className="text-sm text-text-tertiary" data-id="element-3691">
                                  {' '}
                                  / {student.enrollments.length} total
                                </span>
                              </td>
                              <td className="px-6 py-4" data-id="element-3692">
                                <span className="text-sm font-medium text-text-primary" data-id="element-3693">
                                  {student.guardian ? '1' : '0'}
                                </span>
                              </td>
                              <td className="px-6 py-4" data-id="element-3694">
                                {student.status === 'enrolled' ? <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#00B75F] whitespace-nowrap" data-id="element-3695">
                                    <div className="w-2 h-2 rounded-full bg-[#00B75F]" data-id="element-3696" />
                                    Enrolled
                                  </span> : <span className="inline-flex items-center gap-1.5 text-sm font-medium text-text-tertiary whitespace-nowrap" data-id="element-3697">
                                    Not Enrolled
                                  </span>}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>}
              </div>
            </main>
            <Footer data-id="element-3698" />
          </div>
        </div>

        {/* Student Detail Panel */}
        <StudentProfilePanel isOpen={showStudentPanel} student={selectedStudent} onClose={() => setShowStudentPanel(false)} showEndEnrollment={false} data-id="element-3699" />
      </div>
    </Tooltip.Provider>;
}